import { useNavigate } from "react-router-dom";
import { ImpactCardsGrid } from "../../../impactCardsGrid/ImpactCardsGrid";
import { Card } from "../../../ui/common/card";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { PAGE_SIZE_OPTIONS, UNVERSIONED_ROW_KEY, VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { resolveVersionHistoryAccess, selectDefaultRecordId } from "../helpers/versionHistory.helpers";
import { resolveDisplayedReason, resolveVersionLabel } from "../utils/versionHistory.utils";
import { formatCalculatedVersionLabel } from "../utils/versionHistory.utils";
import { useGroupedVersionHistory } from "../hooks/useGroupedVersionHistory";
import { useVersionActivation } from "../hooks/useVersionActivation";
import { useVersionDeletion } from "../hooks/useVersionDeletion";
import { useVersionDrawingChanges } from "../hooks/useVersionDrawingChanges";
import { useVersionHistoryPagination } from "../hooks/useVersionHistoryPagination";
import { useVersionRename } from "../hooks/useVersionRename";
import { useCompanyCurrencyCode } from "../hooks/useCompanyCurrencyCode";
import { useVersionDetailsEditor } from "../hooks/useVersionDetailsEditor";
import { useVersionRowExpansion } from "../hooks/useVersionRowExpansion";
import { VersionHistoryGroup } from "../types/versionHistory.types";
import { ActivateVersionDialog } from "./ActivateVersionDialog";
import { DeleteVersionDetailsDialog } from "./DeleteVersionDetailsDialog";
import { DeleteVersionDialog } from "./DeleteVersionDialog";
import { VersionChangeReasonBox } from "./VersionChangeReasonBox";
import { VersionHistoryPaginationBar } from "./VersionHistoryPaginationBar";
import { VersionHistoryStateMessage } from "./VersionHistoryStateMessage";
import { VersionHistoryTable } from "./VersionHistoryTable";

export interface VersionHistoryPanelProps {
  /** Product whose mapping versions are listed. */
  productId: string | undefined;
  /** Sales unit scope; also the mapping `sourceId` used for navigation and drawing-changes. */
  salesUnitId: string | undefined;
  /** Average products have no per-sales-unit mapping versions; renders the gated message. */
  isAverageProduct: boolean;
  /** Called after a successful activate (restore) so the host can refetch its own summary data. */
  onVersionRestored?: (restoreResult: unknown) => void;
  /** Called after a successful rename / reporting-year change. Optional. */
  onVersionMetadataChanged?: () => void;
  /** Optional heading; hosts that already render a modal title pass `null`. */
  heading?: string | null;
  className?: string;
}

export const VersionHistoryPanel = ({
  productId,
  salesUnitId,
  isAverageProduct,
  onVersionRestored,
  onVersionMetadataChanged,
  heading = VERSION_HISTORY_COPY.panelTitle,
  className
}: VersionHistoryPanelProps) => {
  const navigate = useNavigate();
  const access = resolveVersionHistoryAccess({ isAverageProduct });

  const { pageNumber, pageSize, setPageNumber, setPageSize } = useVersionHistoryPagination({ productId, salesUnitId });

  const { versions, total, isLoading, isFetching, isError, refetch } = useGroupedVersionHistory({
    productId,
    salesUnitId,
    pageNumber,
    pageSize,
    enabled: access.isAllowed
  });

  const {
    expandedVersionIds,
    selectedVersionId,
    selectedRecordIdByVersion,
    toggleVersion,
    expandVersion,
    selectRecord
  } = useVersionRowExpansion({ versions, seedScope: `${productId}|${salesUnitId}|${pageNumber}` });

  const currencyCode = useCompanyCurrencyCode();
  const rename = useVersionRename({ onSaved: () => onVersionMetadataChanged?.(), refetchVersions: refetch });
  const detailsEditor = useVersionDetailsEditor({
    onSaved: () => onVersionMetadataChanged?.(),
    refetchVersions: refetch
  });
  const activation = useVersionActivation({ onActivated: onVersionRestored, refetchVersions: refetch });
  const deletion = useVersionDeletion({ onDeleted: onVersionMetadataChanged, refetchVersions: refetch });

  /** Editing happens inside the expanded row, so opening the editor must reveal it. */
  const startEditDetails = (version: VersionHistoryGroup) => {
    detailsEditor.startEdit(version);
    if (version.versionId) expandVersion(version.versionId);
  };

  const selectedVersion =
    versions.find(version => (version.versionId ?? UNVERSIONED_ROW_KEY) === selectedVersionId) ?? null;

  const selectedRecordId = selectedVersionId
    ? (selectedRecordIdByVersion[selectedVersionId] ?? selectDefaultRecordId(selectedVersion ?? undefined))
    : undefined;
  const selectedRecord = selectedVersion?.records.find(record => record.id === selectedRecordId);

  const drawingChanges = useVersionDrawingChanges({ record: selectedRecord, ownerId: productId, sourceId: salesUnitId });

  if (!access.isAllowed) {
    return <VersionHistoryStateMessage tone="warning" message={access.message ?? ""} />;
  }

  const goToMapping = (version: VersionHistoryGroup) => {
    navigate(`/mapping/${MappingSourceType.product}?sourceId=${salesUnitId}&versionId=${version.versionId}`);
  };

  return (
    <div className={className}>
      <div className="tw-space-y-4">
        {heading ? (
          <div>
            <h3 className="tw-text-base tw-font-semibold">{heading}</h3>
            <p className="tw-text-sm tw-text-muted-foreground">{VERSION_HISTORY_COPY.panelDescription}</p>
          </div>
        ) : null}

        <Card className="tw-border-border tw-p-0 tw-gap-0 tw-overflow-x-auto">
          <VersionHistoryTable
            versions={versions}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={() => refetch()}
            expandedVersionIds={expandedVersionIds}
            selectedRecordIdByVersion={selectedRecordIdByVersion}
            editingVersionId={rename.editingVersionId}
            isSavingName={rename.isSaving}
            currencyCode={currencyCode}
            editingDetailsVersionId={detailsEditor.editingVersionId}
            isSavingDetails={detailsEditor.isSaving}
            isActivating={activation.isActivating}
            isDeleting={deletion.isDeleting}
            onToggleVersion={toggleVersion}
            onSelectRecord={selectRecord}
            onStartRename={rename.startRename}
            onCommitRename={rename.commit}
            onCancelRename={rename.cancel}
            onStartEditDetails={startEditDetails}
            onCommitDetails={detailsEditor.commit}
            onCancelEditDetails={detailsEditor.cancel}
            onRequestDeleteDetails={detailsEditor.requestDelete}
            onGoToMapping={goToMapping}
            onRequestActivate={activation.requestActivate}
            onRequestDelete={deletion.requestDelete}
          />
          {total > 0 ? (
            <VersionHistoryPaginationBar
              pageNumber={pageNumber}
              pageSize={pageSize}
              total={total}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setPageNumber}
              onPageSizeChange={setPageSize}
            />
          ) : null}
        </Card>

        {selectedVersion ? (
          <VersionChangeReasonBox
            versionLabel={resolveVersionLabel(selectedVersion)}
            reason={resolveDisplayedReason(selectedRecord, selectedVersion)}
            changes={drawingChanges.changes}
            isLoading={drawingChanges.isLoading}
            isError={drawingChanges.isError}
            showAllChanges={drawingChanges.showAllChanges}
            onToggleShowAllChanges={drawingChanges.toggleShowAllChanges}
          />
        ) : null}

        {selectedRecord && selectedVersion ? (
          <ImpactCardsGrid
            impactData={selectedRecord.totalImpact}
            contextLabel={`${
              selectedVersion.versionName ?? `Version ${selectedVersion.versionNumber}`
            } · ${formatCalculatedVersionLabel(selectedRecord.calculateVersion)}`}
          />
        ) : null}
      </div>

      <ActivateVersionDialog
        version={activation.versionToActivate}
        isActivating={activation.isActivating}
        onConfirm={activation.confirmActivate}
        onCancel={activation.cancelActivate}
      />

      <DeleteVersionDetailsDialog
        version={detailsEditor.versionToDelete}
        isDeleting={detailsEditor.isDeleting}
        onConfirm={detailsEditor.confirmDelete}
        onCancel={detailsEditor.cancelDelete}
      />

      <DeleteVersionDialog
        version={deletion.versionToDelete}
        versions={versions}
        isDeleting={deletion.isDeleting}
        onConfirm={deletion.confirmDelete}
        onCancel={deletion.cancelDelete}
      />
    </div>
  );
};
